/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import React, { useEffect, useState, useCallback, useRef } from 'react';

import { ARCHIVE_PAGES_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceStop } from '@confluence/experience-tracker/entry-points/component';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { SPACE_ARCHIVED_PAGES } from '@confluence/named-routes';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';

import type {
	ArchivePagesLoaderQuery as ArchivePagesLoaderQuery$data,
	ArchivePagesLoaderQueryVariables as ArchivePagesLoaderQuery$variables,
} from './__types__/ArchivePagesLoaderQuery';
import { ArchiveDialogBaseComponent } from './ArchiveDialogBaseComponent';
import { ArchivePagesMutation } from './ArchivePagesMutation.graphql';
import type {
	ArchiveDialogProps,
	ArchiveDialogPropsWithContentId,
} from './ArchiveProcessContainer';
import { ArchiveProcessContainer } from './ArchiveProcessContainer';
import { ArchiveConfirmationMessage } from './ArchiveConfirmationMessage';
import { EditionFeatureGate } from './EditionFeatureGate';
import type { ArchivePagesMutationVariables } from './__types__/ArchivePagesMutation';
import { DescendantsNoteApplicationOption } from './__types__/ArchivePagesMutation';
import { ArchivedPageNoteInput } from './ArchivedPageNoteInput';
import type { UnarchivePagesMutationVariables } from './__types__/UnarchivePagesMutation';
import { archivePagesRestrictionWarningsI18n } from './ArchivePagesRestrictionsConstants';
import { RestrictionsWarningMessage } from './RestrictionsWarningMessage';

const ARCHIVE_NESTED_CONFIRMATION_DIALOG_SOURCE: string = `archiveNestedPagesConfirmationDialog`;
const ARCHIVE_SINGLE_CONFIRMATION_DIALOG_SOURCE = `archiveSinglePageConfirmationDialog`;

type ArchivePagesLoaderProps = {
	contentId: string;
	source: ArchiveDialogProps<ArchivePagesMutationVariables>['source'];
	onSuccess?: () => void;
	onLinkClick?: () => void;
};

type ArchivePagesLoaderMutationVariables =
	| ArchivePagesMutationVariables
	| UnarchivePagesMutationVariables;

export function ArchivePagesLoader({
	contentId,
	source,
	onSuccess,
	onLinkClick,
}: ArchivePagesLoaderProps) {
	const { data, error } = useQuery<ArchivePagesLoaderQuery$data, ArchivePagesLoaderQuery$variables>(
		gql`
			query ArchivePagesLoaderQuery($contentId: ID!) {
				singleContent(id: $contentId) {
					id
					type
					space {
						alias
						id
						key
					}
					hasVisibleChildPages
					visibleDescendantsCount
					hasRestrictions
					hasInheritedRestrictions
				}
			}
		`,
		{
			variables: { contentId },
			fetchPolicy: 'network-only',
		},
	);
	const [hasChildren, setHasChildren] = useState<boolean | undefined>(undefined);

	useEffect(() => {
		// When we have query data, only compute the value of hasChildren and set into local
		// state ONCE. This is because on archive success, we re-query for the page's children
		// (they may have changed) to properly reset the Apollo cache for "restore" functionality.
		// A change in the cached data will cause this component to rerender and in some cases,
		// auto-trigger a second archive attempt). This doesn't throw an error but shows two success
		//flags to the user which is confusing. So, rely on a one-time, locally stored value of
		// hasChildren as long as this component is mounted.
		if (data && hasChildren === undefined) {
			setHasChildren(data?.singleContent?.hasVisibleChildPages || false);
		}
	}, [data, hasChildren]);

	const spaceKey = data?.singleContent?.space?.key;
	const spaceAlias = data?.singleContent?.space?.alias || spaceKey;
	const spaceId = data?.singleContent?.space?.id || '';
	const visibleDescendantsCount = data?.singleContent?.visibleDescendantsCount || 0;
	const pageCount = visibleDescendantsCount + 1;
	const isContentTypePage = data?.singleContent?.type === 'page';
	const hasOwnRestrictions = data?.singleContent?.hasRestrictions || false;
	const hasInheritedRestrictions = data?.singleContent?.hasInheritedRestrictions || false;

	// source is used for analytics
	const TriggerComponentWithAllowNestedPages = useCallback(
		(props: ArchiveDialogProps<ArchivePagesLoaderMutationVariables>) => {
			const targetEditions = [ConfluenceEdition.PREMIUM, ConfluenceEdition.STANDARD];
			// If page has children, warn the user in the nested archive dialog;
			// otherwise, show them the single page dialog.
			if (hasChildren) {
				return (
					<EditionFeatureGate targetEditions={targetEditions}>
						{({ isFeatureEnabled: allowNestedPages }) => (
							<ArchivePageWithChildrenDialog
								{...props}
								source={source}
								allowNestedPages={allowNestedPages}
								contentId={contentId}
								isContentTypePage={isContentTypePage}
								hasInheritedRestrictions={hasInheritedRestrictions}
								hasOwnRestrictions={hasOwnRestrictions}
							/>
						)}
					</EditionFeatureGate>
				);
			} else {
				return (
					<ArchiveSinglePage
						{...props}
						source={source}
						contentId={contentId}
						isContentTypePage={isContentTypePage}
						hasInheritedRestrictions={hasInheritedRestrictions}
					/>
				);
			}
		},
		[
			source,
			hasChildren,
			contentId,
			hasInheritedRestrictions,
			hasOwnRestrictions,
			isContentTypePage,
		],
	);

	if (error) {
		return (
			<>
				<ExperienceStop name={ARCHIVE_PAGES_EXPERIENCE} error={error} />
				<ErrorDisplay error={error} />
			</>
		);
	}

	return hasChildren !== undefined && spaceAlias ? (
		<ArchiveProcessContainer<ArchivePagesLoaderMutationVariables>
			contentId={contentId}
			analyticsSource={
				hasChildren
					? ARCHIVE_NESTED_CONFIRMATION_DIALOG_SOURCE
					: ARCHIVE_SINGLE_CONFIRMATION_DIALOG_SOURCE
			}
			isArchive
			mutation={ArchivePagesMutation}
			source={source}
			spaceAlias={spaceAlias}
			spaceId={spaceId}
			TriggerComponent={TriggerComponentWithAllowNestedPages}
			onSuccess={onSuccess}
			onSuccessLinkClick={onLinkClick}
			pageCount={pageCount}
			featureName="archivePages"
		/>
	) : null;
}

function ArchivePageWithChildrenDialog(
	props: ArchiveDialogPropsWithContentId<ArchivePagesLoaderMutationVariables> & {
		allowNestedPages?: boolean;
		isContentTypePage?: boolean;
		hasInheritedRestrictions: boolean;
		hasOwnRestrictions: boolean;
	},
) {
	const {
		contentId,
		isArchive,
		spaceAlias,
		allowNestedPages = false,
		analyticsSource,
		isContentTypePage,
		hasInheritedRestrictions,
		hasOwnRestrictions,
	} = props;

	const [includeNestedPages, setIncludeNestedPages] = useState<boolean | null>(true);
	const [descendantsNoteApplicationOption, setDescendantsNoteApplicationOption] =
		useState<DescendantsNoteApplicationOption>(DescendantsNoteApplicationOption.NONE);
	const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
	const noteRef = useRef<HTMLTextAreaElement>(null);

	const getAdditionalAnalyticsAttributes = useCallback(() => {
		const includedNote = !!noteRef?.current?.value;
		return { includedNote };
	}, [noteRef]);
	const getAdditionalMutationVariables = useCallback(() => {
		return {
			archiveNote: noteRef?.current?.value,
			descendantsNoteApplicationOption,
			areChildrenIncluded: Boolean(includeNestedPages),
		};
	}, [descendantsNoteApplicationOption, includeNestedPages, noteRef]);

	const [isNestedActionsDialogNotePageEnabled, setIsNestedActionsDialogNotePageEnabled] =
		useState(false);
	return (
		<ArchiveDialogBaseComponent
			{...props}
			isArchive={isArchive}
			getAdditionalMutationVariables={getAdditionalMutationVariables}
			getAdditionalAnalyticsAttributes={getAdditionalAnalyticsAttributes}
			successLinkHref={SPACE_ARCHIVED_PAGES.toUrl({ spaceKey: spaceAlias })}
			appearance={allowNestedPages ? 'primary' : 'warning'}
			width={allowNestedPages ? 600 : undefined}
			allowNestedPages={allowNestedPages}
			includeNestedPages={includeNestedPages}
			analyticsSource={analyticsSource}
			isSubmitDisabled={isSubmitDisabled}
			shouldUseAutomationDiscovery={isContentTypePage}
			setIsNestedActionsDialogNotePageEnabled={setIsNestedActionsDialogNotePageEnabled}
			isNestedActionsDialogNotePageEnabled={isNestedActionsDialogNotePageEnabled}
		>
			{isNestedActionsDialogNotePageEnabled ? (
				<ArchivedPageNoteInput
					noteRef={noteRef}
					analyticsSource={analyticsSource}
					onValidate={(isValid) => {
						setIsSubmitDisabled(!isValid);
					}}
					isArchiveInProgress={props.isArchiveInProgress}
				/>
			) : (
				<ArchiveConfirmationMessage
					allowNestedPages={allowNestedPages}
					includeNestedPages={includeNestedPages}
					setIncludeNestedPages={setIncludeNestedPages}
					descendantsNoteApplicationOption={descendantsNoteApplicationOption}
					setDescendantsNoteApplicationOption={setDescendantsNoteApplicationOption}
					contentId={contentId}
					isArchive={isArchive}
					analyticsSource={analyticsSource}
					noteRef={noteRef}
					onNoteValidate={(isValid) => {
						setIsSubmitDisabled(!isValid);
					}}
					isArchiveInProgress={props.isArchiveInProgress}
					hasInheritedRestrictions={hasInheritedRestrictions}
					hasOwnRestrictions={hasOwnRestrictions}
				/>
			)}
		</ArchiveDialogBaseComponent>
	);
}

function ArchiveSinglePage(
	props: ArchiveDialogPropsWithContentId<ArchivePagesLoaderMutationVariables> & {
		isContentTypePage?: boolean;
		hasInheritedRestrictions: boolean;
	},
) {
	const { isArchive, spaceAlias, analyticsSource, isContentTypePage, hasInheritedRestrictions } =
		props;
	const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
	const noteRef = useRef<HTMLTextAreaElement>(null);
	const getAdditionalAnalyticsAttributes = useCallback(() => {
		const includedNote = !!noteRef?.current?.value;
		return { includedNote };
	}, []);
	const getAdditionalMutationVariables = useCallback(() => {
		return {
			archiveNote: noteRef?.current?.value,
			descendantsNoteApplicationOption: DescendantsNoteApplicationOption.NONE,
			areChildrenIncluded: false,
		};
	}, [noteRef]);

	return (
		<ArchiveDialogBaseComponent
			{...props}
			isArchive={isArchive}
			getAdditionalMutationVariables={getAdditionalMutationVariables}
			getAdditionalAnalyticsAttributes={getAdditionalAnalyticsAttributes}
			successLinkHref={SPACE_ARCHIVED_PAGES.toUrl({ spaceKey: spaceAlias })}
			width={600}
			allowNestedPages={false}
			includeNestedPages={false}
			isSingleArchive
			analyticsSource={analyticsSource}
			isSubmitDisabled={isSubmitDisabled}
			shouldUseAutomationDiscovery={isContentTypePage}
		>
			{/* If the page has inherited restrictions, show a warning message about it losing its
          restrictions in the archive. */}
			{hasInheritedRestrictions && (
				<RestrictionsWarningMessage
					title={archivePagesRestrictionWarningsI18n.inheritedRestrictionsPageOnlyTitle}
					message={archivePagesRestrictionWarningsI18n.inheritedRestrictionsPageOnlyMessage}
					hasMessagePadding={false}
				/>
			)}
			<ArchivedPageNoteInput
				noteRef={noteRef}
				analyticsSource={analyticsSource}
				onValidate={(isValid) => {
					setIsSubmitDisabled(!isValid);
				}}
				isArchiveInProgress={props.isArchiveInProgress}
			/>
		</ArchiveDialogBaseComponent>
	);
}
